import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Button, Card, Col, Form, Row, Table, Spinner, Badge,
} from "react-bootstrap";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { FaPlusCircle, FaMinusCircle, FaExchangeAlt, FaFileExcel, FaWallet } from "react-icons/fa";
import { PERIOD_OPTIONS, buildPeriodParams, getPeriodLabel } from "./utils/periodFilter";
import { buildChips, countActiveChips, formatDateChip, resolveMapLabel, resolveOptionLabel } from "./utils/accountingFilterUtils";
import AccountingFilterSection from "./components/AccountingFilterSection";
import AccountingActiveFilters from "./components/AccountingActiveFilters";
import "./accountingFilters.css";
import "./accountingResponsive.css";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import axiosInstance from "../../axiosInstance";
import AdLayout from "../../components/AdLayout";
import { Pagination } from "../../components/ui";
import { formatVND as fmtVND, formatMoney } from "./utils/moneyFormat";

const PAGE_SIZE = 30;
const unwrap = (res) => res?.data?.data ?? res?.data ?? {};
const DEFAULT_PERIOD = "MONTH";

const buildDefaultFilters = () => ({
  bankAccountId: "",
  type: "",
  lineId: "",
  period: DEFAULT_PERIOD,
  ...buildPeriodParams(DEFAULT_PERIOD),
});

const TXN_LABELS = {
  INFLOW: { label: "Thu", bg: "success", sign: "+" },
  OUTFLOW: { label: "Chi", bg: "danger", sign: "-" },
};

const fmtShort = (v) => {
  const n = Number(v || 0);
  const abs = Math.abs(n);
  if (abs >= 1e9) return (n / 1e9).toFixed(1) + " tỷ";
  if (abs >= 1e6) return (n / 1e6).toFixed(1) + " tr";
  return formatMoney(n);
};

const cardStyle = { boxShadow: "0 2px 8px rgba(0,0,0,0.1)" };

const isCanceled = (err) =>
  err?.code === "ERR_CANCELED"
  || err?.name === "CanceledError"
  || err?.name === "AbortError";

const buildParams = (filters, page) => {
  const params = { page, size: PAGE_SIZE };
  if (filters.bankAccountId) params.bankAccountId = filters.bankAccountId;
  if (filters.type) params.type = filters.type;
  if (filters.lineId) params.lineId = filters.lineId;
  if (filters.dateFrom) params.dateFrom = filters.dateFrom;
  if (filters.dateTo) params.dateTo = filters.dateTo;
  return params;
};

const buildReportParams = (filters) => {
  const params = {};
  if (filters.bankAccountId) params.bankAccountId = filters.bankAccountId;
  if (filters.type) params.type = filters.type;
  if (filters.lineId) params.lineId = filters.lineId;
  if (filters.dateFrom) params.dateFrom = filters.dateFrom;
  if (filters.dateTo) params.dateTo = filters.dateTo;
  return params;
};

const SummaryCard = ({ title, value, color, icon: Icon, loading, onClick, active = false }) => {
  const clickable = typeof onClick === "function";
  const handleKeyDown = (e) => {
    if (!clickable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick(e);
    }
  };
  return (
    <Card
      className="h-100 acc-kpi-compact"
      onClick={clickable ? onClick : undefined}
      onKeyDown={handleKeyDown}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-pressed={clickable ? active : undefined}
      style={{
        ...cardStyle,
        borderLeft: `4px solid ${color}`,
        opacity: loading ? 0.55 : 1,
        cursor: clickable ? "pointer" : "default",
        outline: active ? "2px solid #0d6efd" : "none",
        outlineOffset: active ? "1px" : 0,
        transition: "outline 0.12s ease",
      }}
    >
      <Card.Body className="py-2 px-2">
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: 32, height: 32, borderRadius: 8, flexShrink: 0,
              background: `${color}18`, color, display: "flex",
              alignItems: "center", justifyContent: "center", fontSize: "0.85rem",
            }}
          >
            <Icon />
          </div>
          <div className="min-w-0 flex-grow-1">
            <div className="acc-kpi-title text-truncate" title={title}>{title}</div>
            <div className="acc-kpi-value text-truncate" style={{ color }} title={loading ? "…" : String(fmtVND(value))}>
              {loading ? "…" : fmtVND(value)}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff", border: "1px solid #e9ecef", borderRadius: 8,
      padding: "10px 14px", boxShadow: "0 4px 12px rgba(0,0,0,.1)", fontSize: "0.75rem",
    }}
    >
      <div className="fw-bold mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {fmtVND(p.value)}
        </div>
      ))}
    </div>
  );
};

const CashFlowJournal = ({ embedded = false }) => {
  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [chartMetric, setChartMetric] = useState("both"); // both | inflow | outflow | net
  const [report, setReport] = useState(null);

  const [bankAccounts, setBankAccounts] = useState([]);
  const [lines, setLines] = useState([]);

  const initialFilters = useMemo(() => buildDefaultFilters(), []);
  const [filterBank, setFilterBank] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterLine, setFilterLine] = useState("");
  const [filterFrom, setFilterFrom] = useState(initialFilters.dateFrom || "");
  const [filterTo, setFilterTo] = useState(initialFilters.dateTo || "");
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [filterOpen, setFilterOpen] = useState(false);

  const fetchSeqRef = useRef(0);
  const abortRef = useRef(null);
  const filtersRef = useRef(appliedFilters);
  filtersRef.current = appliedFilters;

  const chipDefs = useMemo(() => [
    {
      key: "period",
      label: "Thời gian",
      isActive: (v) => v && v !== DEFAULT_PERIOD,
      formatValue: (v, applied) => (
        v === "CUSTOM"
          ? formatDateChip(applied.dateFrom, applied.dateTo)
          : PERIOD_OPTIONS.find((o) => o.value === v)?.label || v
      ),
    },
    {
      key: "bankAccountId",
      label: "TK Ngân hàng",
      isActive: (v) => Boolean(v),
      formatValue: (v) => {
        const b = bankAccounts.find((a) => String(a.id) === String(v));
        return b ? `${b.bankName} - ${b.accountNumber}` : v;
      },
    },
    {
      key: "type",
      label: "Loại GD",
      isActive: (v) => Boolean(v),
      formatValue: (v) => resolveMapLabel(v, TXN_LABELS),
    },
    {
      key: "lineId",
      label: "Line",
      isActive: (v) => Boolean(v),
      formatValue: (v) => resolveOptionLabel(v, lines),
    },
  ], [bankAccounts, lines]);

  const activeChips = useMemo(
    () => buildChips(appliedFilters, chipDefs),
    [appliedFilters, chipDefs]
  );

  const fetchDropdowns = useCallback(async () => {
    try {
      const [bRes, lRes] = await Promise.all([
        axiosInstance.get("/api/accounting/bank-accounts/active").catch(() => null),
        axiosInstance.get("/api/accounting/cash-flows/lines").catch(() => null),
      ]);
      const bankData = unwrap(bRes);
      const lineData = unwrap(lRes);
      if (bankData) setBankAccounts(Array.isArray(bankData) ? bankData : []);
      if (lineData) setLines(Array.isArray(lineData) ? lineData : []);
    } catch (_) { /* silent */ }
  }, []);

  const fetchData = useCallback(async (p = 0, filters) => {
    const nextFilters = filters || filtersRef.current;
    const seq = ++fetchSeqRef.current;
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const listParams = buildParams(nextFilters, p);
      const reportParams = buildReportParams(nextFilters);
      const [res, reportRes] = await Promise.all([
        axiosInstance.get("/api/accounting/cash-flows", {
          params: listParams,
          signal: controller.signal,
        }),
        axiosInstance.get("/api/accounting/cash-flows/report", {
          params: reportParams,
          signal: controller.signal,
        }),
      ]);
      if (seq !== fetchSeqRef.current) return;

      const body = unwrap(res);
      const reportBody = unwrap(reportRes);
      setRows(body.content || []);
      setTotalPages(body.totalPages || 0);
      setTotalElements(body.totalElements || 0);
      setReport(reportBody && typeof reportBody === "object" ? reportBody : null);
      setPage(p);
    } catch (err) {
      if (isCanceled(err) || seq !== fetchSeqRef.current) return;
      toast.error("Không thể tải dữ liệu dòng tiền");
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchDropdowns();
    const defaults = buildDefaultFilters();
    setAppliedFilters(defaults);
    setPeriod(defaults.period);
    setFilterFrom(defaults.dateFrom || "");
    setFilterTo(defaults.dateTo || "");
    fetchData(0, defaults);
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = (next) => {
    setAppliedFilters(next);
    filtersRef.current = next;
    // Clear stale numbers immediately so UI never mixes old totals with new period label
    setReport(null);
    setRows([]);
    setTotalPages(0);
    setTotalElements(0);
    setPage(0);
    fetchData(0, next);
  };

  const syncDraftFromApplied = (filters) => {
    setFilterBank(filters.bankAccountId || "");
    setFilterType(filters.type || "");
    setFilterLine(filters.lineId || "");
    setPeriod(filters.period || DEFAULT_PERIOD);
    setFilterFrom(filters.dateFrom || "");
    setFilterTo(filters.dateTo || "");
  };

  const buildNextFilters = () => {
    const range = period === "CUSTOM"
      ? { dateFrom: filterFrom, dateTo: filterTo }
      : buildPeriodParams(period);
    return {
      bankAccountId: filterBank,
      type: filterType,
      lineId: filterLine,
      period,
      dateFrom: range.dateFrom || "",
      dateTo: range.dateTo || "",
    };
  };

  const handleSearch = () => applyFilters(buildNextFilters());

  const handlePeriodChange = (nextPeriod) => {
    setPeriod(nextPeriod);
    if (nextPeriod !== "CUSTOM") {
      const range = buildPeriodParams(nextPeriod);
      setFilterFrom(range.dateFrom || "");
      setFilterTo(range.dateTo || "");
    }
  };

  const handleReset = () => {
    const defaults = buildDefaultFilters();
    syncDraftFromApplied(defaults);
    applyFilters(defaults);
  };

  const handleRemoveChip = (key) => {
    const base = { ...appliedFilters };
    if (key === "period") {
      const defaults = buildDefaultFilters();
      Object.assign(base, defaults);
      base.bankAccountId = appliedFilters.bankAccountId;
      base.type = appliedFilters.type;
      base.lineId = appliedFilters.lineId;
    } else {
      base[key] = "";
    }
    syncDraftFromApplied(base);
    applyFilters(base);
  };

  const exportExcel = () => {
    if (!rows.length) { toast.warn("Không có dữ liệu để xuất"); return; }
    const ws = XLSX.utils.json_to_sheet(
      rows.map((r, i) => ({
        STT: i + 1 + page * PAGE_SIZE,
        "Ngày": r.transactionDate || "",
        "Loại": TXN_LABELS[r.transactionType]?.label || r.transactionType,
        "Mã phiếu": r.voucherCode || "",
        "TK Ngân hàng": r.bankAccountName || "",
        "Đối tượng": r.entityName || "",
        "Line": r.lineName || "",
        "Số tiền": r.amount || 0,
        "Mô tả": r.description || "",
        "Số dư sau GD": r.balanceAfter || 0,
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DongTien");
    XLSX.writeFile(wb, "QuanLyDongTien.xlsx");
  };


  const chartData = (report?.dailyTrend || []).map((d) => ({
    date: d.date?.slice(5) || d.date,
    fullDate: d.date,
    Thu: Number(d.inflow || 0),
    Chi: Number(d.outflow || 0),
    "Ròng": Number(d.inflow || 0) - Number(d.outflow || 0),
  }));

  const periodLabel = getPeriodLabel(appliedFilters.period);
  const rangeHint = appliedFilters.dateFrom && appliedFilters.dateTo
    ? `${appliedFilters.dateFrom} → ${appliedFilters.dateTo}`
    : "";

  const body = (
    <div className={embedded ? "" : "acc-page"}>
      {!embedded && (
        <div className="mb-3">
          <h5 className="fw-bold mb-1">Dòng tiền</h5>
          <small className="text-muted">Theo dõi luồng tiền thu — chi qua phiếu thu/chi đã xác nhận</small>
        </div>
      )}

      <Card className="mb-3 acc-filter-card">
        <Card.Body className="p-0">
          <AccountingFilterSection
            open={filterOpen}
            onToggle={setFilterOpen}
            onSubmit={handleSearch}
            onReset={handleReset}
            activeCount={countActiveChips(activeChips)}
            toolbarRight={(
              <Button variant="success" size="sm" onClick={exportExcel} disabled={loading || !rows.length}>
                <FaFileExcel className="me-1" /> Xuất Excel
              </Button>
            )}
          >
            <Row className="g-2 align-items-end acc-filter-grid">
              <Col>
                <Form.Label className="small mb-1">Thời gian</Form.Label>
                <Form.Select size="sm" value={period} onChange={(e) => handlePeriodChange(e.target.value)}>
                  {PERIOD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </Col>
              {period === "CUSTOM" && (
                <>
                  <Col>
                    <Form.Label className="small mb-1">Từ ngày</Form.Label>
                    <Form.Control type="date" size="sm" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
                  </Col>
                  <Col>
                    <Form.Label className="small mb-1">Đến ngày</Form.Label>
                    <Form.Control type="date" size="sm" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
                  </Col>
                </>
              )}
              <Col>
                <Form.Label className="small mb-1">TK Ngân hàng</Form.Label>
                <Form.Select size="sm" value={filterBank} onChange={(e) => setFilterBank(e.target.value)}>
                  <option value="">Tất cả</option>
                  {bankAccounts.map((b) => (
                    <option key={b.id} value={b.id}>{b.bankName} - {b.accountNumber}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col>
                <Form.Label className="small mb-1">Loại GD</Form.Label>
                <Form.Select size="sm" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                  <option value="">Tất cả</option>
                  <option value="INFLOW">Thu</option>
                  <option value="OUTFLOW">Chi</option>
                </Form.Select>
              </Col>
              <Col>
                <Form.Label className="small mb-1">Line</Form.Label>
                <Form.Select size="sm" value={filterLine} onChange={(e) => setFilterLine(e.target.value)}>
                  <option value="">Tất cả</option>
                  {lines.map((l) => (
                    <option key={l.id} value={l.id}>{l.name || l.lineName}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </AccountingFilterSection>
          <AccountingActiveFilters
            chips={activeChips}
            onRemove={handleRemoveChip}
            onClearAll={handleReset}
          />
        </Card.Body>
      </Card>

      {rangeHint && (
        <div className="text-muted small mb-2">
          Kỳ báo cáo: <strong>{periodLabel}</strong> ({rangeHint})
        </div>
      )}

      <div className="acc-summary-grid--equal">
        <div className="acc-summary-card-wrap">
          <SummaryCard title={`Tổng thu (${periodLabel})`} value={report?.totalInflow} color="#198754" icon={FaPlusCircle} loading={loading} onClick={() => setChartMetric((m) => (m === "inflow" ? "both" : "inflow"))} active={chartMetric === "inflow"} />
        </div>
        <div className="acc-summary-card-wrap">
          <SummaryCard title={`Tổng chi (${periodLabel})`} value={report?.totalOutflow} color="#dc3545" icon={FaMinusCircle} loading={loading} onClick={() => setChartMetric((m) => (m === "outflow" ? "both" : "outflow"))} active={chartMetric === "outflow"} />
        </div>
        <div className="acc-summary-card-wrap">
          <SummaryCard title="Dòng tiền ròng" value={report?.netFlow} color="#0d6efd" icon={FaExchangeAlt} loading={loading} onClick={() => setChartMetric((m) => (m === "net" ? "both" : "net"))} active={chartMetric === "net"} />
        </div>
        <div className="acc-summary-card-wrap">
          <SummaryCard title="Số dư TK hiện tại" value={report?.currentBalance} color="#6f42c1" icon={FaWallet} loading={loading} />
        </div>
      </div>

      {loading && (
        <Card className="mb-3" style={cardStyle}>
          <Card.Body className="text-center py-4">
            <Spinner animation="border" variant="primary" size="sm" className="me-2" />
            Đang tải dữ liệu kỳ {periodLabel}…
          </Card.Body>
        </Card>
      )}
      {!loading && chartData.length > 0 && (
        <Card className="mb-3" style={cardStyle}>
          <Card.Body>
            <h6 className="fw-semibold mb-3">
              {chartMetric === "inflow"
                ? `Biểu đồ dòng thu theo ngày (${periodLabel})`
                : chartMetric === "outflow"
                  ? `Biểu đồ dòng chi theo ngày (${periodLabel})`
                  : chartMetric === "net"
                    ? `Biểu đồ dòng tiền ròng theo ngày (${periodLabel})`
                    : `Biểu đồ thu — chi theo ngày (${periodLabel})`}
            </h6>
            <div className="acc-chart-wrap">
              <ResponsiveContainer width="100%" height="100%">
                {chartMetric === "net" ? (
                  <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={fmtShort} tick={{ fontSize: 12 }} width={70} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend />
                    <Line type="monotone" dataKey="Ròng" stroke="#0d6efd" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                ) : (
                  <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={fmtShort} tick={{ fontSize: 12 }} width={70} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend />
                    {chartMetric !== "outflow" && (
                      <Bar dataKey="Thu" fill="#198754" radius={[4, 4, 0, 0]} maxBarSize={40} />
                    )}
                    {chartMetric !== "inflow" && (
                      <Bar dataKey="Chi" fill="#dc3545" radius={[4, 4, 0, 0]} maxBarSize={40} />
                    )}
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          </Card.Body>
        </Card>
      )}

      <Card style={cardStyle}>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-5 text-muted">Không có giao dịch dòng tiền trong kỳ đã chọn</div>
          ) : (
            <>
              <div className="acc-table-wrap">
                <Table striped hover className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Ngày</th>
                      <th>Loại</th>
                      <th className="acc-col-hide-sm">Loại Thu/Chi</th>
                      <th>Mã phiếu</th>
                      <th className="acc-col-hide-sm">TK Ngân hàng</th>
                      <th className="acc-col-hide-sm">Đối tượng</th>
                      <th className="acc-col-hide-sm">Line</th>
                      <th className="text-end">Số tiền</th>
                      <th className="acc-col-hide-sm">Mô tả</th>
                      <th className="text-end acc-col-hide-sm">Số dư sau GD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => {
                      const txn = TXN_LABELS[r.transactionType] || {};
                      const isInflow = r.transactionType === "INFLOW";
                      return (
                        <tr key={r.id || idx}>
                          <td>{r.transactionDate}</td>
                          <td>
                            <Badge bg={txn.bg || "secondary"}>{txn.label || r.transactionType}</Badge>
                          </td>
                          <td className="small acc-col-hide-sm">{r.businessTypeLabel || "—"}</td>
                          <td className="fw-semibold text-muted">{r.voucherCode || "—"}</td>
                          <td className="acc-col-hide-sm">{r.bankAccountName || "—"}</td>
                          <td className="acc-col-hide-sm">{r.entityName || "—"}</td>
                          <td className="acc-col-hide-sm">{r.lineName || "—"}</td>
                          <td className={`text-end fw-semibold ${isInflow ? "text-success" : "text-danger"}`}>
                            {txn.sign || ""}{fmtVND(r.amount)}
                          </td>
                          <td className="text-muted acc-col-hide-sm" style={{ maxWidth: 200 }}>{r.description || "—"}</td>
                          <td className="text-end acc-col-hide-sm">{fmtVND(r.balanceAfter)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              <div className="acc-table-footer">
                <small className="text-muted">
                  {totalElements} giao dịch
                  {report?.totalTransactions != null && (
                    <> · Thu {fmtVND(report.totalInflow)} · Chi {fmtVND(report.totalOutflow)}</>
                  )}
                </small>
                <Pagination
                  hidePaginationWhenSinglePage
                  showItemsPerPage={false}
                  pageData={{ page: { number: page, totalPages, totalElements, size: PAGE_SIZE } }}
                  onPageChange={(p) => !loading && fetchData(p - 1)}
                />
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );

  if (embedded) return body;
  return <AdLayout>{body}</AdLayout>;
};

export default CashFlowJournal;
